import { List, useTable } from "@refinedev/antd";
import { useQuery } from "@tanstack/react-query";
import { CanAccess, useCan, useNotification } from "@refinedev/core";
import {
  Alert,
  Button,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router";

import { api } from "../api";
import { DATE_TIME_FORMAT, ORDER_STATUS, ORDER_TYPES, formatKurus } from "../labels";
import type { OrderItem, OrderRefundQuote } from "../types";

const VIEW_OPTIONS = [
  { value: "paid", label: "Ödenen" },
  { value: "pending", label: "Bekleyen" },
  { value: "failed", label: "Başarısız" },
  { value: "refunded", label: "İade" },
  { value: "all", label: "Tümü" },
];

function filterValue(filters: ReturnType<typeof useTable>["filters"], field: string) {
  const filter = filters.find((item) => "field" in item && item.field === field);
  return (filter?.value as string | undefined) ?? "";
}

/**
 * /orders "Öğrenci ödemeleri" (product decision 2026-10-14; permission `orders.view`): the
 * students' package payments through Stripe, with a link to the payment in Stripe's Dashboard.
 * "İade et" (`orders.refund`) gives the amount staff choose back (the refund policy's amount is
 * suggested: hedefuni.com/iptal-ve-iade) and ends the package at once.
 */
export function OrdersPage() {
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });
  const [refunding, setRefunding] = useState<OrderItem | null>(null);
  const { tableProps, tableQuery, filters, setFilters } = useTable<OrderItem>({
    resource: "orders",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "view", operator: "eq", value: "paid" }] },
    syncWithLocation: true,
  });
  const view = filterValue(filters, "view") || "paid";
  const search = filterValue(filters, "search");
  const sumKurus = (tableQuery.data as { sumKurus?: number } | undefined)?.sumKurus ?? 0;

  const setFilter = (field: "view" | "search", value: string) =>
    setFilters(
      [
        { field: "view", operator: "eq", value: field === "view" ? value : view },
        { field: "search", operator: "eq", value: field === "search" ? value : search },
      ],
      "replace",
    );

  return (
    <List title="Öğrenci ödemeleri">
      <Typography.Paragraph type="secondary">
        Paket ödemeleri Stripe üzerinden alınır. Otomatik yenilemeler kayıtlı karttan çekilir;
        "Bekleyen" ödemeler öğrencinin kart formunda yarım bıraktıklarıdır.
      </Typography.Paragraph>
      <Space wrap align="center" style={{ marginBottom: 16 }}>
        <Segmented
          options={VIEW_OPTIONS}
          value={view}
          onChange={(value) => setFilter("view", String(value))}
        />
        <Input.Search
          allowClear
          placeholder="Öğrenci adı veya e-posta"
          defaultValue={search}
          onSearch={(value) => setFilter("search", value)}
          style={{ width: 260 }}
        />
        <Statistic
          title={`Toplam (${tableQuery.data?.total ?? 0} ödeme)`}
          value={formatKurus(sumKurus)}
          valueStyle={{ fontSize: 18 }}
        />
      </Space>
      <Table {...tableProps} rowKey="id" scroll={{ x: "max-content" }}>
        <Table.Column<OrderItem>
          title="Tarih"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
        />
        <Table.Column<OrderItem>
          title="Öğrenci"
          dataIndex="student"
          render={(student: OrderItem["student"]) =>
            canSeeStudents?.can ? (
              <Link to={`/students/${student.id}`}>{student.name}</Link>
            ) : (
              student.name
            )
          }
        />
        <Table.Column<OrderItem>
          title="Paket"
          dataIndex="plan"
          render={(plan: string, order) => (
            <Space direction="vertical" size={0}>
              {plan}
              <Typography.Text type="secondary">{ORDER_TYPES[order.type]}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<OrderItem>
          title="Tutar"
          dataIndex="amountKurus"
          render={(value: number) => <Typography.Text strong>{formatKurus(value)}</Typography.Text>}
        />
        <Table.Column<OrderItem>
          title="Durum"
          dataIndex="status"
          render={(value: string, order) => (
            <Space direction="vertical" size={0}>
              <Tag color={ORDER_STATUS[value]?.color}>{ORDER_STATUS[value]?.label ?? value}</Tag>
              {order.refundedAt ? (
                <Typography.Text type="secondary">
                  {dayjs(order.refundedAt).format(DATE_TIME_FORMAT)}
                  {order.refundedKurus !== null && order.refundedKurus < order.amountKurus
                    ? ` · ${formatKurus(order.refundedKurus)} iade`
                    : ""}
                </Typography.Text>
              ) : null}
              {order.guardianApproved ? (
                <Typography.Text type="secondary">Veli onaylı</Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<OrderItem>
          title=""
          key="actions"
          render={(_, order) => (
            <Space>
              {order.stripeUrl ? (
                <Button size="small" href={order.stripeUrl} target="_blank" rel="noreferrer">
                  Stripe'ta aç
                </Button>
              ) : null}
              {order.status === "PAID" ? (
                <CanAccess resource="orders" action="refund">
                  <Button size="small" danger onClick={() => setRefunding(order)}>
                    İade et
                  </Button>
                </CanAccess>
              ) : null}
            </Space>
          )}
        />
      </Table>

      <RefundDialog
        order={refunding}
        onClose={() => setRefunding(null)}
        onRefunded={() => tableQuery.refetch()}
      />
    </List>
  );
}

const RULES: Record<
  OrderRefundQuote["rule"],
  { type: "success" | "info" | "warning"; text: string }
> = {
  FULL: { type: "success", text: "Kurala göre tamamı iade edilir: paket kullanılmamış." },
  PARTIAL: {
    type: "info",
    text: "Kurala göre kullanılan ders saatleri düşülerek iade edilir: ilk hafta içinde.",
  },
  NONE: {
    type: "warning",
    text: "Kurala göre iade yok: süre geçmiş. Yalnızca bizden kaynaklı bir sorunda iade et.",
  },
};

function RefundDialog({
  order,
  onClose,
  onRefunded,
}: {
  order: OrderItem | null;
  onClose: () => void;
  onRefunded: () => void | Promise<unknown>;
}) {
  const { open } = useNotification();
  const [reason, setReason] = useState("");
  // Read fresh each time the dialog opens: hours are booked and questions sent meanwhile.
  const { data: quote, error: quoteError } = useQuery({
    queryKey: ["orders", order?.id, "refund-quote"],
    queryFn: () => api<OrderRefundQuote>(`/api/office/orders/${order!.id}/refund-quote`),
    enabled: order !== null,
    staleTime: 0,
    gcTime: 0,
  });
  // Whole lira in the field; kuruş to the API. Until staff type, the policy's amount.
  const [typed, setTyped] = useState<number | null | undefined>(undefined);
  const suggested = quote?.suggestedKurus ? quote.suggestedKurus / 100 : null;
  const lira = typed === undefined ? suggested : typed;
  const [busy, setBusy] = useState(false);

  function close() {
    setReason("");
    setTyped(undefined);
    onClose();
  }

  const amountKurus = lira === null ? null : Math.round(lira * 100);
  const valid =
    order !== null &&
    amountKurus !== null &&
    amountKurus >= 100 &&
    amountKurus <= order.amountKurus &&
    reason.trim().length >= 3;

  async function refund() {
    if (!order || amountKurus === null) return;
    setBusy(true);
    try {
      const result = await api<{ packageEnded: boolean; refundedKurus: number }>(
        `/api/office/orders/${order.id}/refund`,
        { method: "POST", body: { reason, amountKurus } },
      );
      open?.({
        type: "success",
        message: `${formatKurus(result.refundedKurus)} iade edildi${
          result.packageEnded ? " ve öğrencinin paketi sona erdi" : ""
        }.`,
      });
      await onRefunded();
      close();
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Ödemeyi iade et"
      open={order !== null}
      onCancel={close}
      destroyOnHidden
      okText={amountKurus ? `${formatKurus(amountKurus)} iade et` : "İade et"}
      okButtonProps={{ danger: true, disabled: !valid, loading: busy }}
      cancelText="Vazgeç"
      onOk={() => void refund()}
    >
      {order ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            {order.student.name} · {order.plan} · {ORDER_TYPES[order.type]}
          </Typography.Text>
          {quote ? (
            <>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="Ödenen">
                  {formatKurus(quote.amountKurus)} · {dayjs(quote.paidAt).format(DATE_TIME_FORMAT)}{" "}
                  ({dayjs().diff(dayjs(quote.paidAt), "day")} gün önce)
                </Descriptions.Item>
                <Descriptions.Item label="Kullanılan ders saati">
                  {quote.hoursUsed}
                  {quote.hoursBought !== null ? ` / ${quote.hoursBought}` : ""}
                </Descriptions.Item>
                <Descriptions.Item label="Gönderilen soru">{quote.questionsUsed}</Descriptions.Item>
                <Descriptions.Item label="Kurala göre iade">
                  {quote.suggestedKurus === null
                    ? "Hesaplanamıyor (yükseltme): kullanılan kısmı düşerek gir"
                    : formatKurus(quote.suggestedKurus)}
                </Descriptions.Item>
              </Descriptions>
              <Alert type={RULES[quote.rule].type} showIcon message={RULES[quote.rule].text} />
            </>
          ) : quoteError ? (
            <Alert type="error" showIcon message={quoteError.message} />
          ) : (
            <Typography.Text type="secondary">İade kuralı hesaplanıyor…</Typography.Text>
          )}
          <InputNumber
            min={1}
            max={order.amountKurus / 100}
            step={10}
            precision={2}
            addonBefore="İade tutarı"
            addonAfter="₺"
            style={{ width: "100%" }}
            value={lira}
            onChange={(value) => setTyped(value)}
          />
          <Alert
            type="warning"
            showIcon
            message="Tutar karta iade edilir ve öğrencinin paketi hemen sona erer."
            description="Haftalık saatleri kalmaz; alınmış dersleri yerinde kalır. Bu işlem geri alınamaz."
          />
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="İade nedeni (kayıtlara yazılır)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Space>
      ) : null}
    </Modal>
  );
}
